import { Box, Flex, useToast } from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useCallback } from 'react';

import { portfolioCovenants as commonPortfolioCovenants } from '@finley-technologies/covenant-calculations';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { IntermediateFieldsQueryVariablesSidebar } from '../covenantIntermediateField/IntermediateFieldsQueryVariablesSidebar';
import { IntermediateFieldsView } from '../covenantIntermediateField/IntermediateFieldView';
import { CovenantValueContainer } from '../covenantValue/CovenantValueView';
import { ThresholdIntermediateFieldsView } from '../covenantValue/ThresholdValueContainer';
import { WipConfig } from 'packages/creditAgreements/ui/types';

type PortfolioCovenantEditCovenantValueContainerProps = {
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  triggerDate: Util.FullDateStr;
  intermediateFields: CreditAgreement.Config.CommonIntermediateField.IntermediateField[];
};
export const PortfolioCovenantEditCovenantValueContainer = ({
  wipConfig,
  fullPropertyKey: portfolioCovenantFullPropertyKey,
  triggerDate,
  intermediateFields,
}: PortfolioCovenantEditCovenantValueContainerProps) => {
  const toast = useToast();

  const onSave = useCallback(
    async ({
      field,
      saveIdx,
      fullPropertyKey,
      configPropertyKey,
      setIsSaveLoading,
    }: {
      field: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
      saveIdx: number;
      fullPropertyKey: string;
      configPropertyKey: string;
      setIsSaveLoading?: React.Dispatch<React.SetStateAction<boolean>>;
    }) => {
      // trim leading "=", formula engine does not detect this error until runtime.
      const sanitizedField: CreditAgreement.Config.CommonIntermediateField.IntermediateField = field.config.formula.startsWith(
        '=',
      )
        ? {
            ...field,
            config: { ...field.config, formula: field.config.formula.slice(1) },
          }
        : field;

      if (wipConfig) {
        // cannot simulateously update the specific intermediate field
        // and then check the entire _config_ in singular method
        // so we are copying the _config_, updating the specific intermediate field,
        // then checking for errors.
        // finally, we just update the specific intermediate field in the form props
        const config = _.cloneDeep(
          getIn(wipConfig.formProps.values, configPropertyKey), // portfolioCovenants[idx]._config_
        ) as CreditAgreement.Config.PortfolioCovenant.Config;

        config.intermediateFields = config.intermediateFields.map(
          (intField, index) => (index === saveIdx ? sanitizedField : intField),
        );

        const withTriggerDate = commonPortfolioCovenants.attachTriggerDate({
          config,
          triggerDate,
        });

        if (!withTriggerDate.config) {
          toast({
            title: 'Cannot save invalid intermediate field configuration.',
            description: withTriggerDate.errors.join(', '),
            status: 'error',
            position: 'top',
            isClosable: true,
          });
          return;
        }

        const errors = commonPortfolioCovenants.validatePortfolioCovenantConfig(
          { config: withTriggerDate.config },
        );

        if (errors.length) {
          toast({
            title: 'Cannot save invalid intermediate field configuration.',
            description: errors.join(', '),
            status: 'error',
            position: 'top',
            isClosable: true,
          });
          return;
        }

        await wipConfig.saveAndUpdateConfigCreditAgreementWip({
          wipConfig,
          propertyKey: fullPropertyKey,
          updatedProperty: sanitizedField,
          setIsSaveLoading,
        });
      }
    },
    [wipConfig, triggerDate, toast],
  );

  const configPropertyKey = `${portfolioCovenantFullPropertyKey}._config_`;
  const intermediateFieldPropertyKey = `${portfolioCovenantFullPropertyKey}._config_.intermediateFields`;

  return (
    <Flex>
      <Box width="100%">
        <IntermediateFieldsView
          wipConfig={wipConfig}
          intermediateFields={intermediateFields}
          configPropertyKey={configPropertyKey}
          intermediateFieldPropertyKey={intermediateFieldPropertyKey}
          onSave={onSave}
        />
        <CovenantValueContainer
          covenantType="portfolio"
          wipConfig={wipConfig}
          intermediateFields={intermediateFields}
          configPropertyKey={configPropertyKey}
          intermediateFieldPropertyKey={intermediateFieldPropertyKey}
          onSave={onSave}
        />
        <ThresholdIntermediateFieldsView
          covenantType="portfolio"
          wipConfig={wipConfig}
          intermediateFields={intermediateFields}
          configPropertyKey={configPropertyKey}
          intermediateFieldPropertyKey={intermediateFieldPropertyKey}
          onSave={onSave}
        />
      </Box>
      {!!wipConfig && wipConfig.operationMode === 'client' ? (
        <IntermediateFieldsQueryVariablesSidebar
          covenantType="portfolio"
          wipConfig={wipConfig}
          configPropertyKey={configPropertyKey}
        />
      ) : null}
    </Flex>
  );
};
